import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FaTimes } from "react-icons/fa";
import { DatePicker, TimePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import moment from "moment-timezone";
import "antd/dist/reset.css";
import "./AddAvailabilityModal.css";
import "./AddAvailabilityDrawer.css";

dayjs.extend(customParseFormat);

const WEEKDAY_OPTIONS = [
  { label: "S", value: "sunday" },
  { label: "M", value: "monday" },
  { label: "T", value: "tuesday" },
  { label: "W", value: "wednesday" },
  { label: "T", value: "thursday" },
  { label: "F", value: "friday" },
  { label: "S", value: "saturday" },
];

const END_TYPES = [
  { label: "Never", value: "never" },
  { label: "ON", value: "on" },
];

const PICKER_POPUP_CONTAINER = () => document.body;

const toDayjsTime = (time) => {
  if (!time) return null;
  if (dayjs.isDayjs(time)) return time.isValid() ? time : null;
  const raw = String(time).trim();
  const parsed = dayjs(raw, ["HH:mm:ss", "HH:mm", "h:mm A"], true);
  return parsed.isValid() ? parsed : dayjs(`2000-01-01T${raw.slice(0, 5)}`);
};

const toDayjsDate = (value) => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value.startOf("day") : null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return dayjs(value).startOf("day");
  }
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const parsed = dayjs(raw.slice(0, 10), "YYYY-MM-DD", true);
    return parsed.isValid() ? parsed.startOf("day") : null;
  }
  const parsed = dayjs(
    raw,
    ["MM/DD/YYYY", "M/D/YYYY", "YYYY-MM-DD", "MM-DD-YYYY"],
    true,
  );
  return parsed.isValid() ? parsed.startOf("day") : null;
};

const formatUsDate = (date) => {
  const d = toDayjsDate(date);
  return d ? d.format("MM/DD/YYYY") : null;
};

const toTimeKey = (time) => {
  if (!time) return "";
  if (dayjs.isDayjs(time)) return time.format("HH:mm:ss");
  const raw = String(time).trim();
  if (/^\d{1,2}:\d{2}/.test(raw)) {
    const [h, m, s] = raw.split(":");
    return `${h.padStart(2, "0")}:${m}:${s || "00"}`;
  }
  return "";
};

const toDateKey = (value) => {
  const d = toDayjsDate(value);
  return d ? d.format("YYYY-MM-DD") : "";
};

const slotRangesOverlap = (dateA, startA, endA, dateB, startB, endB) => {
  const start1 = moment(`${dateA}T${toTimeKey(startA)}`);
  let end1 = moment(`${dateA}T${toTimeKey(endA)}`);
  if (!end1.isAfter(start1)) end1 = end1.clone().add(1, "day");

  const dateBKey = toDateKey(dateB) || String(dateB || "").slice(0, 10);
  const start2 = moment(`${dateBKey}T${toTimeKey(startB)}`);
  let end2 = moment(`${dateBKey}T${toTimeKey(endB)}`);
  if (!end2.isAfter(start2)) end2 = end2.clone().add(1, "day");

  return start1.isBefore(end2) && start2.isBefore(end1);
};

const formatDurationLabel = (start, end) => {
  if (!start || !end || !end.isAfter(start)) return "";
  const mins = end.diff(start, "minute");
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  if (!hours) return `${minutes}m`;
  if (!minutes) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

const AddAvailabilityDrawer = ({
  existingSlots = [],
  initialData = {},
  isOpen,
  isSubmitting,
  onClose,
  onSubmit,
}) => {
  const [availabilityName, setAvailabilityName] = useState("My Availability");
  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringInterval, setRecurringInterval] = useState(1);
  const [selectedDays, setSelectedDays] = useState([]);
  const [endType, setEndType] = useState("never");
  const [endsOn, setEndsOn] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;

    const dateValue =
      toDayjsDate(initialData.startDate) || dayjs().startOf("day");
    const start = toDayjsTime(initialData.startTime);
    const end = toDayjsTime(initialData.endTime);
    const weekday = dateValue.format("dddd").toLowerCase();

    setAvailabilityName(
      initialData.title ||
        initialData.name ||
        initialData.notes ||
        "My Availability",
    );
    const defaultStart = start || dayjs().hour(9).minute(0).second(0);
    const defaultEnd =
      end ||
      (defaultStart ? defaultStart.add(1, "hour") : dayjs().hour(10).minute(0));

    setStartDate(dateValue);
    setStartTime(defaultStart);
    setEndTime(defaultEnd);
    setIsRecurring(Boolean(initialData.isRecurring));
    setRecurringInterval(Number(initialData.recurringInterval) || 1);
    setSelectedDays(
      Array.isArray(initialData.selectedDays) && initialData.selectedDays.length
        ? initialData.selectedDays
        : [weekday],
    );
    setEndType(initialData.endType === "on" ? "on" : "never");
    setEndsOn(toDayjsDate(initialData.endsOn));
    setErrors({});
  }, [isOpen, initialData]);

  const handleEndTypeChange = (value) => {
    setEndType(value);
    if (value === "on") {
      setEndsOn((prev) => prev || startDate || dayjs().startOf("day"));
    } else {
      setEndsOn(null);
    }
  };

  if (!isOpen) return null;

  const toggleDay = (value) => {
    setSelectedDays((prev) =>
      prev.includes(value)
        ? prev.filter((d) => d !== value)
        : [...prev, value],
    );
  };

  const handleStartDateChange = (date) => {
    const next = toDayjsDate(date);
    setStartDate(next);
    if (next) {
      const weekday = next.format("dddd").toLowerCase();
      setSelectedDays((prev) =>
        prev.includes(weekday) ? prev : [...prev, weekday],
      );
    }
  };

  const durationLabel = formatDurationLabel(startTime, endTime);

  const validate = () => {
    const next = {};
    if (!availabilityName.trim()) {
      next.availabilityName = "Availability name is required.";
    }
    if (!startDate) next.startDate = "Date is required.";
    if (!startTime) next.startTime = "Start time is required.";
    if (!endTime) next.endTime = "End time is required.";
    if (startTime && endTime && !endTime.isAfter(startTime)) {
      next.endTime = "End time must be after start time.";
    }
    if (isRecurring && selectedDays.length === 0) {
      next.selectedDays = "Select at least one day.";
    }
    if (isRecurring && (recurringInterval < 1 || recurringInterval > 12)) {
      next.recurringInterval = "Interval must be between 1 and 12.";
    }
    if (isRecurring && endType === "on") {
      if (!endsOn) next.endsOn = "End date is required.";
      else if (startDate && endsOn.isBefore(startDate, "day")) {
        next.endsOn = "End date must be on or after the start date.";
      }
    }
    if (
      Object.keys(next).length === 0 &&
      startDate &&
      startTime &&
      endTime
    ) {
      const dayKey = startDate.format("YYYY-MM-DD");
      const startKey = startTime.format("HH:mm:ss");
      const endKey = endTime.format("HH:mm:ss");
      const overlap = existingSlots.find((slot) =>
        slotRangesOverlap(
          dayKey,
          startKey,
          endKey,
          slot.startDate,
          slot.startTime,
          slot.endTime,
        ),
      );
      if (overlap) {
        next.overlap =
          "This availability overlaps with an existing block. Please adjust your hours.";
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    onSubmit({
      name: availabilityName.trim(),
      notes: availabilityName.trim() || null,
      title: availabilityName.trim() || "Available",
      startDate: formatUsDate(startDate),
      endDate: formatUsDate(startDate),
      startTime: startTime.format("HH:mm:ss"),
      endTime: endTime.format("HH:mm:ss"),
      isRecurring,
      recurringInterval: isRecurring ? Number(recurringInterval) || 1 : 1,
      recurringUnit: "week",
      selectedDays: isRecurring
        ? selectedDays
        : [startDate.format("dddd").toLowerCase()],
      endType: isRecurring ? endType : "never",
      endsOn:
        isRecurring && endType === "on" && endsOn
          ? formatUsDate(endsOn)
          : null,
      endOccurrences: null,
      timezone: moment.tz.guess(),
    });
  };

  return createPortal(
    <div className="pp-availability-drawer-overlay" onClick={onClose}>
      <aside
        className="pp-availability-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pp-add-availability-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pp-availability-drawer-header">
          <h2 id="pp-add-availability-title">Add Availability</h2>
          <button
            type="button"
            className="pp-availability-drawer-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes size={14} />
          </button>
        </div>

        <form className="pp-availability-drawer-form" onSubmit={handleSubmit}>
          <div className="pp-availability-drawer-body availability-form">
            <div className="form-section">
              <label className="form-label" htmlFor="pp-avail-name">
                Availability Name
              </label>
              <input
                id="pp-avail-name"
                type="text"
                className={`form-input ${errors.availabilityName ? "error" : ""}`}
                value={availabilityName}
                onChange={(e) => setAvailabilityName(e.target.value)}
                placeholder="My Availability"
              />
              {errors.availabilityName && (
                <span className="error-message">{errors.availabilityName}</span>
              )}
            </div>

            <div className="form-section">
              <div className="datetime-range pp-availability-datetime-range">
                <div className="datetime-input">
                  <div className="input-wrapper date-picker">
                    <DatePicker
                      className="pp-availability-antd-date"
                      format="MM/DD/YYYY"
                      value={startDate}
                      onChange={handleStartDateChange}
                      placeholder="Select date"
                      allowClear={false}
                      inputReadOnly
                      getPopupContainer={PICKER_POPUP_CONTAINER}
                      placement="bottomLeft"
                    />
                  </div>
                  <div className="bar" />
                  <div className="input-wrapper time-picker">
                    <TimePicker
                      format="h:mm A"
                      minuteStep={15}
                      use12Hours
                      value={startTime}
                      onChange={setStartTime}
                      placeholder="Start time"
                      allowClear={false}
                      needConfirm={false}
                      getPopupContainer={PICKER_POPUP_CONTAINER}
                      placement="bottomLeft"
                    />
                  </div>
                </div>

                <span className="range-separator">to</span>

                <div className="datetime-input">
                  <div className="input-wrapper time-picker">
                    <TimePicker
                      format="h:mm A"
                      minuteStep={15}
                      use12Hours
                      value={endTime}
                      onChange={setEndTime}
                      placeholder="End time"
                      allowClear={false}
                      needConfirm={false}
                      getPopupContainer={PICKER_POPUP_CONTAINER}
                      placement="bottomLeft"
                    />
                  </div>
                </div>
              </div>
              {(errors.startDate || errors.startTime || errors.endTime) && (
                <span className="error-message">
                  {errors.startDate || errors.startTime || errors.endTime}
                </span>
              )}
            </div>

            <div className="form-section">
              <label className="form-label" htmlFor="pp-avail-duration">
                Duration
              </label>
              <input
                id="pp-avail-duration"
                type="text"
                className="form-input pp-availability-duration-input"
                value={durationLabel || "—"}
                readOnly
                tabIndex={-1}
              />
            </div>

            <div className="form-section">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  className="checkbox-input"
                  checked={isRecurring}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                />
                <span className="checkbox-text">Recurring Availability</span>
              </label>
            </div>

            {isRecurring && (
              <div className="recurring-section">
                <div className="recurring-row">
                  <span className="recurring-label">Every</span>
                  <select
                    className={`form-select recurring-number ${
                      errors.recurringInterval ? "error" : ""
                    }`}
                    value={recurringInterval}
                    onChange={(e) =>
                      setRecurringInterval(Number(e.target.value))
                    }
                  >
                    {[1, 2, 3, 4].map((num) => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </select>
                  <select
                    className="form-select is-static"
                    value="week"
                    disabled
                  >
                    <option value="week">Week</option>
                  </select>
                </div>
                {errors.recurringInterval && (
                  <span className="error-message">
                    {errors.recurringInterval}
                  </span>
                )}

                <div className="days-selection">
                  {WEEKDAY_OPTIONS.map((opt, index) => (
                    <button
                      key={`${opt.value}-${index}`}
                      type="button"
                      className={`day-button ${
                        selectedDays.includes(opt.value) ? "selected" : ""
                      }`}
                      onClick={() => toggleDay(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {errors.selectedDays && (
                  <span className="error-message">{errors.selectedDays}</span>
                )}

                <div className="recurring-row pp-availability-ends-row">
                  <span className="recurring-label">Ends</span>
                  <select
                    className="form-select ends-on-select"
                    value={endType}
                    onChange={(e) => handleEndTypeChange(e.target.value)}
                    aria-label="Recurring end condition"
                  >
                    {END_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {endType === "on" && (
                    <div className="pp-availability-ends-date-wrap">
                      <DatePicker
                        className="pp-availability-ends-date"
                        format="MM/DD/YYYY"
                        value={endsOn}
                        onChange={(date) => setEndsOn(toDayjsDate(date))}
                        placeholder="Select end date"
                        allowClear={false}
                        inputReadOnly
                        getPopupContainer={PICKER_POPUP_CONTAINER}
                        placement="bottomLeft"
                        disabledDate={(current) =>
                          startDate
                            ? current && current.isBefore(startDate, "day")
                            : false
                        }
                      />
                    </div>
                  )}
                </div>
                {errors.endsOn && (
                  <span className="error-message">{errors.endsOn}</span>
                )}
              </div>
            )}

            {errors.overlap && (
              <span className="error-message">{errors.overlap}</span>
            )}
          </div>

          <div className="pp-availability-drawer-footer">
            <button
              type="button"
              className="pp-availability-btn-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="pp-availability-btn-save"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Add Availability"}
            </button>
          </div>
        </form>
      </aside>
    </div>,
    document.body,
  );
};

export default AddAvailabilityDrawer;
